import { Button } from '@1trading/react/components/ui/button';
import { useFormStepper } from '@1trading/react/components/ui/form-stepper';
import { EditIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import type { AppropriatenessTest } from '@/features/futures/schemas/appropriateness-test';
import { getStepIndex } from '@/features/futures/utils/appropriateness-test';
import { useProfileFuturesAppropriateness } from '@/hooks/queries/use-profile-futures-appropriateness';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';
import type { FuturesAppropriatenessAnswersCompleted } from '@/services/api/profile/futures-appropriateness';

type FuturesAppropriatenessTestSummaryQuestionProps<K extends keyof FuturesAppropriatenessAnswersCompleted> = {
  question: K;
  answer: FuturesAppropriatenessAnswersCompleted[K];
  isEditable: boolean;
};

const QUESTION_EDIT_VISIBILITY_MAP = {
  averageComplexTransactionValue: (answers) => answers.complexTransactionCount !== 'NONE',
} satisfies Partial<Record<keyof FuturesAppropriatenessAnswersCompleted, (answers: FuturesAppropriatenessAnswersCompleted) => boolean>>;

const FuturesAppropriatenessTestSummaryLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('FuturesAppropriatenessAssessment.summary.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FuturesAppropriatenessAssessment.summary.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
FuturesAppropriatenessTestSummaryLegend.displayName = 'FuturesAppropriatenessTestSummaryLegend';

const FuturesAppropriatenessTestSummaryQuestion = <K extends keyof FuturesAppropriatenessAnswersCompleted>({
  question,
  answer,
  isEditable,
}: FuturesAppropriatenessTestSummaryQuestionProps<K>) => {
  const { setStep } = useFormStepper();
  const { i18n, t } = useTranslation();
  const step = getStepIndex(question);

  return (
    <li className="flex gap-4 lg:gap-16 items-center rounded-md p-4 relative bg-background" key={question}>
      <div className="flex-1 space-y-2">
        <h3 className="font-medium">{t(`FuturesAppropriatenessAssessment.${question}.title`)}</h3>
        <p className="text-sm text-muted-foreground">
          {Array.isArray(answer)
            ? answer
                .map((answer) => {
                  if (i18n.exists(`FuturesAppropriatenessAssessment.${question}.options.${answer}.title`)) {
                    return t(`FuturesAppropriatenessAssessment.${question}.options.${answer}.title`);
                  }

                  return t(`FuturesAppropriatenessAssessment.${question}.options.${answer}`);
                })
                .join(', ')
            : t(`FuturesAppropriatenessAssessment.${question}.options.${answer}`)}
        </p>
      </div>
      {isEditable ? (
        <Button
          aria-label={`${t('fundamentals.edit')} ${t(`FuturesAppropriatenessAssessment.${question}.title`)}`}
          className="-mt-2 -mr-2 after:content-[''] after:absolute after:inset-0"
          size="icon"
          variant="link"
          onClick={() => setStep(step)}
        >
          <EditIcon className="size-4" />
        </Button>
      ) : null}
    </li>
  );
};

const FuturesAppropriatenessTestSummary = () => {
  const { data: futuresAppropriateness } = useProfileFuturesAppropriateness();
  const answers = futuresAppropriateness?.answers as FuturesAppropriatenessAnswersCompleted | undefined;

  if (!answers) throw new Error('No answers found for futures appropriateness summary');

  return (
    <ul className="space-y-4">
      {(Object.entries(answers) as [keyof FuturesAppropriatenessAnswersCompleted, FuturesAppropriatenessAnswersCompleted[keyof FuturesAppropriatenessAnswersCompleted]][]).map(
        ([question, answer]) => (
          <FuturesAppropriatenessTestSummaryQuestion
            key={question}
            question={question}
            answer={answer}
            isEditable={!(question in QUESTION_EDIT_VISIBILITY_MAP) || QUESTION_EDIT_VISIBILITY_MAP[question as keyof typeof QUESTION_EDIT_VISIBILITY_MAP](answers)}
          />
        ),
      )}
    </ul>
  );
};
FuturesAppropriatenessTestSummary.displayName = 'FuturesAppropriatenessTestSummary';

const beforeStepLeave: FormStepGuard<AppropriatenessTest> = async ({ direction, form }, next) => {
  if (!form) return next(false);

  if (direction === 'next') form.setValue('confirmation', true);

  return next();
};

FuturesAppropriatenessTestSummary.beforeStepLeave = beforeStepLeave;

export { FuturesAppropriatenessTestSummary, FuturesAppropriatenessTestSummaryLegend };
